import { useEffect, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';

import localStorageUtil from 'utils/localStorageUtil';

const useTableInstance = ({
  tableName,
  data,
  columns,
  getRowId,
  enableSorting = false,
  enablePagination = false,
  enableGlobalFilter = false,
  globalFilterMode = "all",
  globalFilterColumns = [],
  hiddenColumns = [],
  enableColumnPinning = false,
  defaultPinning = { left: [], right: [] },
  enableColumnOrder = false,
  enableRowSelection = false,
}) => {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState(() => {
    const savedSize = localStorageUtil.getItemField('resultsPerPageObject', tableName);
    return {
      pageIndex: 0,
      pageSize: savedSize ? Number(savedSize) : 20,
    };
  });
  const [columnPinning, setColumnPinning] = useState(defaultPinning ?? { left: [], right: [] });
  const [columnOrder, setColumnOrder] = useState(() =>
    columns.map(col => col.id ?? col.accessorKey)
  );
  const [rowSelection, setRowSelection] = useState({});

  useEffect(() => {
    if (!enableColumnOrder) return;

    const columnIds = columns
      .map((column) => column.id ?? column.accessorKey)
      .filter(Boolean);

    setColumnOrder((previousOrder) => {
      const retained = previousOrder.filter((id) => columnIds.includes(id));
      const added = columnIds.filter((id) => !retained.includes(id));
      const nextOrder = [...retained, ...added];

      return nextOrder.length === previousOrder.length &&
        nextOrder.every((id, index) => id === previousOrder[index])
        ? previousOrder
        : nextOrder;
    });
  }, [columns, enableColumnOrder]);

  const columnVisibility = {};
  hiddenColumns.forEach(colId => {
    columnVisibility[colId] = false;
  });

  function columnScopedGlobalFilter(row, _, filterValue) {
    if (!filterValue) return true;
    const search = filterValue.toLowerCase();

    const targetColumns = globalFilterColumns;

    return targetColumns.some(columnId => {
      const cellValue = row.getValue(columnId);
      return String(cellValue ?? "")
        .toLowerCase()
        .includes(search);
    });
  }

  // ColumnFilterToggle values are single select values. Normalize absent data so
  // the UI's "-" option can explicitly filter rows whose field is empty.
  function selectOptionFilter(row, columnId, filterValue) {
    const value = row.getValue(columnId);
    const normalizedValue = value === null || value === undefined || value === ""
      ? "__empty_filter_value__"
      : String(value);

    return normalizedValue === filterValue;
  }

  const table = useReactTable({
    data,
    columns,
    getRowId,
    filterFns: {
      columnScoped: columnScopedGlobalFilter,
      selectOption: selectOptionFilter,
    },
    defaultColumn: {
      filterFn: "selectOption",
    },
    state: {
      ...(enableSorting ? { sorting } : {}),
      ...(enablePagination ? { pagination } : {}),
      ...(enableGlobalFilter ? { globalFilter } : {}),
      ...(enableColumnPinning ? { columnPinning } : {}),
      ...(enableColumnOrder ? { columnOrder } : {}),
      ...(enableRowSelection ? { rowSelection } : {})
    },
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && {
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: setSorting,
    }),
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
    }),
    ...(enableGlobalFilter && {
      getFilteredRowModel: getFilteredRowModel(),
      globalFilterFn: globalFilterMode === "column" ? "columnScoped" : "includesString",
      onGlobalFilterChange: setGlobalFilter,
    }),
    ...(enableColumnPinning && {
      onColumnPinningChange: setColumnPinning,
    }),
    ...(enableColumnOrder && {
      onColumnOrderChange: setColumnOrder,
      // debugTable: true,
      // debugHeaders: true,
      // debugColumns: true,
    }),
    ...(enableRowSelection && {
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
    }),
    initialState: {
      columnVisibility,
      ...(enableColumnPinning ? { columnPinning: defaultPinning } : {}),
      ...(enableColumnOrder ? { columnOrder } : {})
    },
  });

  return {
    table,
    tableName,
    enableSorting,
    enableColumnOrder,
    enablePagination,
    enableGlobalFilter,
    enableColumnPinning,
    ...(enableGlobalFilter ? { globalFilter, setGlobalFilter } : {}),
    ...(enableColumnPinning ? { columnPinning, setColumnPinning } : {}),
    ...(enableColumnOrder ? { columnOrder, setColumnOrder } : {}),
    ...(enableRowSelection ? { rowSelection, setRowSelection } : {})
  };
};

export default useTableInstance;
